import React, { useState, useEffect, useMemo } from "react";
import tw from "tailwind-styled-components";
import { useMutation } from "react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Config from "../../../Config";
import Loading from "../../../Components/Loading";
import { toast } from "react-toastify";
import {
    BoxContainer,
    BoxTitle,
    Underline,
    BoxHead,
    FilterTableWrapper,
    FilterWrapper
} from "../../../Components/Styles/PageStyles";
import NotFoundModel from "../../../Components/NotFoundModel";
import { useSelector } from "react-redux";
import SearchFilterBox from "../../../Components/SearchFilterBox";
import { IoChevronBackOutline } from "react-icons/io5";
import ParticipantsTable from "../../../Components/FoodMenu/ParticipantsTable";

const MealParticipants = () => {
    const searchOptions = [
        { label: "Name", value: 1 },
    ];

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const user = useSelector((state) => state.UserReducer.user);

    // Get params from URL
    const mealDate = searchParams.get('mealDate');
    const propertyId = searchParams.get('propertyId');
    const mealType = searchParams.get('mealType');
    const dietPreference = searchParams.get('dietPreference');

    const [searchQuery, setSearchQuery] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const [selectedData, setSelectedData] = useState("");
    const [apiData, setApiData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);

    const getPageTitle = () => {
        const mealTypeNum = Number(mealType);
        const dietPrefNum = Number(dietPreference);

        let mealLabel = '';
        switch (mealTypeNum) {
            case Config.MEAL_TYPE.BREAKFAST:
                mealLabel = 'Breakfast';
                break;
            case Config.MEAL_TYPE.LUNCH:
                mealLabel = 'Lunch';
                break;
            case Config.MEAL_TYPE.SNACKS:
                mealLabel = 'Snacks';
                break;
            case Config.MEAL_TYPE.DINNER:
                mealLabel = 'Dinner';
                break;
            default:
                mealLabel = 'Meal';
        }

        let dietLabel = '';
        if (dietPrefNum === Config.DIET_PREFERENCE.VEG) {
            dietLabel = 'Veg';
        } else if (dietPrefNum === Config.DIET_PREFERENCE.NON_VEG) {
            dietLabel = 'Non-Veg';
        }

        if (dietLabel) {
            return `${mealLabel} ${dietLabel} Participants`;
        } else {
            return `${mealLabel} Participants`;
        }
    };

    const getFetchFunction = async () => {
        const params = new URLSearchParams();

        params.append('mealDate', mealDate);
        params.append('propId', propertyId);
        params.append('mealType', mealType);
        params.append('dietPreference', dietPreference);
        params.append('pageNum', 1);

        const query = params.toString();

        const response = await axios.get(
            `${Config.apiUrl}/food/tenant/meal/selection/list?${query}`,
            {
                headers: {
                    authorization: "Bearer" + " " + user.token,
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.isSuccess === false) {
            toast.error(res.msg || "An error occurred");
            return;
        } else {
            setApiData(res?.data || []);
            setFilteredData(res?.data || []);
            setIsFocused(true);
        }
    };

    const getError = (res) => {
        toast.error(res.message || "Failed to fetch meal participants");
        console.error("Error fetching meal participants:", res);
    };

    const {
        isLoading,
        mutate: getListMutate,
        error,
        data,
        isRefetching,
    } = useMutation(getFetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });


    const updateMealStatus = async ({ mealId, status }) => {
        const response = await axios.post(
            `${Config.apiUrl}/food/tenant/meal/status/update`,
            {
                mealId: mealId,
                status: status
            },
            {
                headers: {
                    authorization: "Bearer" + " " + user.token,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data;
    };

    const {
        mutate: updateMealStatusMutate,
        isLoading: isUpdatingMeal,
    } = useMutation(updateMealStatus, {
        onSuccess: (res, variables) => {
            if (res.isSuccess) {
                toast.success(res.msg || "Meal status updated successfully");

                setApiData(prevData =>
                    prevData.map(item => {
                        if (item.id === variables.mealId) {
                            return { ...item, status: variables.status };
                        }
                        return item;
                    })
                );

                setFilteredData(prevData =>
                    prevData.map(item => {
                        if (item.id === variables.mealId) {
                            return { ...item, status: variables.status };
                        }
                        return item;
                    })
                );
            } else {
                toast.error(res.msg || "Failed to update meal status");
            }
        },
        onError: (error) => {
            toast.error(error?.response?.data?.msg || "Failed to update meal status");
            console.error("Error updating meal status:", error);
        },
    });

    const handleMealToggle = (mealId, currentStatus) => {
        if (currentStatus === Config.MEAL_CONSUMPTION_STATUS.PENDING) {
            updateMealStatusMutate({
                mealId: mealId,
                status: Config.MEAL_CONSUMPTION_STATUS.CONSUMED
            });
        } else {
            toast.info("This meal has already been " +
                (currentStatus === Config.MEAL_CONSUMPTION_STATUS.CONSUMED ? "consumed" : "skipped") +
                " and cannot be changed");
        }
    };



    useEffect(() => {
        if (!mealDate || !propertyId || !mealType) {
            toast.warning("Missing required parameters");
            return;
        }

        if (user?.token) {
            getListMutate();
        }
    }, [mealDate, propertyId, mealType, dietPreference, user?.token]);

    // Frontend search functionality
    useEffect(() => {
        if (!apiData || apiData.length === 0) {
            setFilteredData([]);
            return;
        }

        if (!searchQuery.trim()) {
            setFilteredData(apiData);
            return;
        }

        const searchTerm = searchQuery.toLowerCase().trim();
        const filtered = apiData.filter((item) => {
            const name = item?.tenantName || '';
            return name.toLowerCase().includes(searchTerm);
        });

        setFilteredData(filtered);
    }, [searchQuery, apiData]);

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleFilterChange = (values) => {
        setSearchQuery(values.searchQuery || '');
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setCurrentPage(1);
    };

    return (
        <>
            <BoxContainer>
                <BoxHead>
                    <BoxTitle className="mb-0 whitespace-nowrap">
                        <div className="flex" onClick={handleGoBack}>
                            <span className="items-center pt-[5px] mr-2 cursor-pointer">
                                <IoChevronBackOutline size={20} />
                            </span>
                            <div className="cursor-pointer">
                                <span className="flex items-center">
                                    {getPageTitle()}
                                </span>
                                <Underline />
                            </div>
                        </div>
                    </BoxTitle>
                </BoxHead>

                <FilterTableWrapper className='mt-4'>
                    <FilterWrapper>
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                            <SearchFilterBox
                                options={searchOptions}
                                handleFilterChange={handleFilterChange}
                                handleClearFilters={handleClearFilters}
                            />
                        </div>
                    </FilterWrapper>

                    {!error && !isLoading && filteredData && (
                        <TableWrapper>
                            <ParticipantsTable
                                apiData={filteredData}
                                setSelectedData={setSelectedData}
                                onMealToggle={handleMealToggle}
                                isUpdatingMeal={isUpdatingMeal}
                            />
                            {(error || filteredData.length === 0) && !isLoading && (
                                <NotFoundModel
                                    msg={searchQuery ? "No matching participants found" : "No Participants Found"}
                                />
                            )}
                        </TableWrapper>
                    )}
                </FilterTableWrapper>

                {error && !isLoading && <NotFoundModel />}
                {(isLoading || isRefetching) && <Loading />}
            </BoxContainer>
        </>
    );
};

const TableWrapper = tw.h1`mt-4 bg-white rounded-md`;

export default MealParticipants;